import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Star1Icon } from '@blade/ui-kit'
import { authStateAtom } from '@root/entities/auth'
import { useTranslation } from '@root/shared/localization'
import { SearchItemsLoupeIcon } from '@root/shared/ui-kit'

import { topbarModeAtom, topbarPositionsModeAtom } from '../atoms'
import { TopbarMode } from '../model/topbar-mode'

import { TopbarSwitchDropdown } from './topbar-switch-dropdown'

export const TopBarSwitch: React.FC = () => {
  const [mode, setMode] = useAtom(topbarModeAtom)
  const [positionsMode, setPositionsMode] = useAtom(topbarPositionsModeAtom)
  const [authState] = useAtom(authStateAtom)
  const { t } = useTranslation()

  const handlePositionsClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (mode !== TopbarMode.Positions) {
      setMode(TopbarMode.Positions)
      e.stopPropagation()
      e.preventDefault()
    }
  }

  if (authState.type !== 'LoggedIn') {
    return <div />
  }

  return (
    <Root>
      <IconGroup>
        <Star1Icon
          size={16}
          aria-checked={mode === TopbarMode.Favorites}
          onClick={() => setMode(TopbarMode.Favorites)}
        />
        <SeparatorVertical />
        <SearchItemsLoupeIcon
          size={16}
          aria-checked={mode === TopbarMode.RecentSearch}
          onClick={() => setMode(TopbarMode.RecentSearch)}
        />
      </IconGroup>
      <TextGroup>
        <Tab
          data-checked={mode === TopbarMode.Trending}
          onClick={() => setMode(TopbarMode.Trending)}
        >
          {t('trending')}
        </Tab>
        <Tab
          data-checked={mode === TopbarMode.Positions}
          onClickCapture={handlePositionsClick}
        >
          <TopbarSwitchDropdown
            active={mode === TopbarMode.Positions}
            items={[
              {
                value: 'recent',
                label: t('topbar.position.recent'),
              },
              {
                value: 'amount',
                label: t('common.amount'),
              },
            ]}
            value={positionsMode}
            onChange={(value) => setPositionsMode(value)}
          />
        </Tab>
      </TextGroup>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
`

const TextGroup = styled.div`
  background: var(--background-1);

  height: 26px;
  padding: 2px;
  border-radius: 6px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;
`

const IconGroup = styled.div`
  background: var(--background-1);

  height: 26px;
  padding: 2px;
  border-radius: 6px;

  display: grid;
  grid-auto-flow: column;
  align-items: center;

  gap: 4px;
  padding-inline: 8px;

  svg {
    color: var(--text-8);
    transition: color 0.2s ease-in-out;
    cursor: pointer;
  }
  svg[aria-checked='true'] {
    color: var(--text-12);
  }
  svg {
    &:hover {
      color: var(--text-12);
    }
  }
`

const Tab = styled.div`
  color: var(--text-11);

  display: grid;
  align-items: center;
  padding-inline: 6px;
  border-radius: 6px;

  height: 22px;
  font-size: 11px;
  font-weight: 600;

  transition:
    color 0.2s ease-in-out,
    background 0.2s ease-in-out;
  cursor: pointer;

  &[data-checked='true'] {
    color: var(--text-12);
    background: var(--appearance-1);
  }

  &[data-checked='false'] {
    &:hover {
      color: var(--appearance-inv-1);
    }
  }
`

const SeparatorVertical = styled.span`
  height: 14px;
  width: 1px;
  border-radius: 2px;
  background: var(--background-2);
`
